/** Harta orașului pe planul de situație: tot orașul ca CÂTEVA căi SVG, una pe
 *  culoare.
 *
 *  Un element pe clădire ar fi mii de noduri în pânză, iar pânza se
 *  redesenează la fiecare cadru de panoramare. Câte o cale pe strat ține
 *  DOM-ul la zece noduri oricât de mare e orașul, ca la machetă, unde
 *  clădirile se strâng pe celule (`geometrie.ts`).
 *
 *  Inelele se orientează (conturul trigonometric, curțile invers) și se umple
 *  cu `nonzero`: curțile ies goale, dar două suprafețe de același fel care se
 *  suprapun (un parc și peluza din el) rămân pline. Cu `evenodd`, suprapunerea
 *  s-ar goli. */

import { orientat } from "./inele";
import type { Inel, Oras3, SursaInaltime } from "./tipuri";

/** Straturile de la sol, de jos în sus — aceeași ordine ca cotele machetei
 *  (`strat3d.ts`, `Z`): strada trece peste verde, calea ferată peste stradă. */
export const SOL_2D = ["verde", "apa", "pietonal", "strada", "caleFerata"] as const;
/** Clădirile, câte un strat pe sursa înălțimii. Ordinea nu contează: serverul
 *  le-a despărțit deja, două clădiri nu ocupă același loc (`suprapuneri.py`). */
export const CLADIRI_2D: readonly SursaInaltime[] = ["osm", "verificat", "estimat"];

export type StratSol2D = (typeof SOL_2D)[number];
export type CaiOras = Record<StratSol2D | SursaInaltime, string>;

/** Zecimetrul ajunge — e și unitatea în care vine harta (`unitate_m`); orice
 *  zecimală în plus doar umflă calea. */
const nr = (v: number): string => String(Math.round(v * 10) / 10);

function inelInCale(inel: Inel, trigonometric: boolean): string {
  const r = orientat(inel, trigonometric);
  if (r.length < 3) return "";
  let d = `M${nr(r[0][0])} ${nr(r[0][1])}`;
  for (let i = 1; i < r.length; i++) d += `L${nr(r[i][0])} ${nr(r[i][1])}`;
  return `${d}Z`;
}

/** Conturul și curțile unui element, ca o bucată de cale. */
export function caleElement(inele: readonly Inel[]): string {
  if (!inele.length) return "";
  return inelInCale(inele[0], true)
    + inele.slice(1).map((g) => inelInCale(g, false)).join("");
}

const cale = (lista: readonly { inele: Inel[] }[]): string =>
  lista.map((e) => caleElement(e.inele)).join("");

/** Toate căile hărții. Se face o dată pe hartă: zoom-ul și panoramarea mută
 *  grupul, nu refac căile. */
export function caiOras(o: Oras3): CaiOras {
  const peSursa = (s: SursaInaltime) => cale(o.cladiri.filter((c) => c.sursa === s));
  return {
    verde: cale(o.verde),
    apa: cale(o.apa),
    pietonal: cale(o.pietonale),
    strada: cale(o.strazi),
    caleFerata: cale(o.caiFerate),
    osm: peSursa("osm"),
    verificat: peSursa("verificat"),
    estimat: peSursa("estimat"),
  };
}
